import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../../utils/api';
import ModulePageHeading from '../../components/ModulePageHeading';
import { resolveNetworkNav } from '../../navigation/networkNavigation';
import { inspectionResultTextClass } from '../../utils/inspectionResultColor';

function formatDate(v) {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleDateString();
  } catch {
    return String(v);
  }
}

export default function SsDateHistory() {
  const { orgId, buildingId, ssCode } = useParams();
  const navigate = useNavigate();
  const decodedSs = decodeURIComponent(ssCode || '');

  const { data, isLoading } = useQuery({
    queryKey: ['service-network', 'ss-date-history', buildingId, decodedSs],
    queryFn: async () =>
      (
        await api.get(
          `/service-network/buildings/${buildingId}/ss/${encodeURIComponent(decodedSs)}/history`
        )
      ).data?.data,
    enabled: !!buildingId && !!decodedSs,
  });

  const building = data?.building;
  const events = data?.events ?? [];
  const titleSs = data?.ss_name ? `${data.ss_code} · ${data.ss_name}` : data?.ss_code || decodedSs;

  return (
    <div>
      <ModulePageHeading
        resolveNav={resolveNetworkNav}
        title={titleSs}
        showBack
        backTo={
          orgId && buildingId
            ? `/network/org/${orgId}/buildings/${buildingId}`
            : orgId
              ? `/network/org/${orgId}`
              : '/network?tab=agent'
        }
        backLabel="Back to building SS"
        infoTitle="SS date history"
        infoChildren={
          <p>
            Date-ordered list of inspections / jobs for this Specified System on this building. Open a row to see the
            inspection detail.
          </p>
        }
      />

      {building?.name && (
        <p className="text-sm text-gray-600 mb-4">
          Building: <span className="font-medium text-gray-900">{building.name}</span>
        </p>
      )}

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : events.length === 0 ? (
        <p className="text-sm text-gray-500 bg-white border rounded-lg p-6 text-center">
          No dated history for this SS on this building yet.
        </p>
      ) : (
        <div className="bg-card rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Job status</th>
                <th className="px-4 py-3">Result</th>
                <th className="px-4 py-3">Due</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {events.map((ev, idx) => (
                <tr
                  key={`${ev.job_id}-${ev.inspection_id || idx}`}
                  className="hover:bg-slate-50 cursor-pointer"
                  onClick={() => ev.job_id && navigate(`/jobs/${ev.job_id}`)}
                >
                  <td className="px-4 py-3 font-medium text-gray-900">{formatDate(ev.date)}</td>
                  <td className="px-4 py-3 text-gray-700">{ev.status || '—'}</td>
                  <td className="px-4 py-3">
                    {ev.overall_status ? (
                      <span className={inspectionResultTextClass(ev.overall_status)}>
                        {ev.overall_status}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-500">{formatDate(ev.due_date)}</td>
                  <td className="px-4 py-3 text-right text-xs text-primary-700">Open →</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
